import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CardMedia,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  useMediaQuery,
  Stack,
  alpha,
  Grow
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  HelpOutline as HelpIcon,
  Discount as DiscountIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  LocalOffer as LocalOfferIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchProducts, createGroupedProduct, getSupportedCurrencies } from '../productsApi';
import config from '../config';
import { useBranch } from '../BranchContext';

// ✅ MOVED OUTSIDE - ProductCard Component
const ProductCard = React.memo(({ 
  product, 
  isSelected, 
  onToggle, 
  getCurrencySymbol, 
  getProductCurrencyPrices,
  isSmallMobile 
}) => {
  const productId = product.id || product.Id;
  
  const prices = getProductCurrencyPrices(product);
  const currencyCodes = Object.keys(prices);
  const primaryCurrency = currencyCodes[0] || 'YER';
  const primaryPrice = prices[primaryCurrency];
  const hasMultipleCurrencies = currencyCodes.length > 1;
  
  let imgUrl = product.imageUrl || product.ImageUrl;
  if (typeof imgUrl === 'object' && imgUrl !== null && !Array.isArray(imgUrl)) {
    if (imgUrl.medium) {
      imgUrl = imgUrl.medium.startsWith('http') ? imgUrl.medium : `${config.API_BASE_URL}${imgUrl.medium}`;
    } else {
      imgUrl = 'https://via.placeholder.com/150';
    }
  } else if (typeof imgUrl === 'string') {
    if (imgUrl.startsWith('/images/')) {
      imgUrl = `${config.API_BASE_URL}${imgUrl}`;
    } else if (!imgUrl) {
      imgUrl = 'https://via.placeholder.com/150';
    }
  } else {
    imgUrl = 'https://via.placeholder.com/150';
  }

  return (
    <Grid item xs={6} sm={4} md={3}>
      <Card 
        sx={{ 
          cursor: 'pointer', 
          border: isSelected ? '2px solid #1976d2' : '1px solid #eee',
          position: 'relative',
          transition: 'all 0.3s ease',
          borderRadius: { xs: 2, sm: 3 },
          '&:hover': {
            boxShadow: 3,
            transform: isSmallMobile ? 'none' : 'translateY(-4px)'
          },
          '&:active': {
            transform: isSmallMobile ? 'scale(0.98)' : 'translateY(-4px)'
          }
        }}
        onClick={() => onToggle(product)}
      >
        {isSelected && (
          <Box sx={{ 
            position: 'absolute', 
            top: 5, 
            right: 5, 
            bgcolor: 'primary.main', 
            color: 'white', 
            borderRadius: '50%', 
            width: { xs: 24, sm: 28 }, 
            height: { xs: 24, sm: 28 }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 2, 
            boxShadow: 2,
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}>
            ✓
          </Box>
        )}
        <Tooltip title={hasMultipleCurrencies ? `${currencyCodes.length} currencies` : primaryCurrency}>
          <Chip
            label={hasMultipleCurrencies ? `${currencyCodes.length} 💰` : primaryCurrency}
            size="small"
            sx={{ 
              position: 'absolute', 
              top: 5, 
              left: 5,
              bgcolor: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              height: { xs: 20, sm: 24 },
              zIndex: 1
            }}
          />
        </Tooltip>
        <CardMedia
          component="img"
          height={isSmallMobile ? "120" : "150"}
          image={imgUrl}
          alt={product.productName || product.ProductName}
          sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
        />
        <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
          <Typography 
            variant="subtitle2" 
            noWrap 
            title={product.productName || product.ProductName}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
          >
            {product.productName || product.ProductName}
          </Typography>
          <Typography 
            variant="body2" 
            color="primary" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {getCurrencySymbol(primaryCurrency)}{primaryPrice}
            {hasMultipleCurrencies && (
              <span style={{fontSize: '0.75em', marginLeft: 4}}>
                +{currencyCodes.length - 1}
              </span>
            )}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
});

export default function GroupedProducts() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [discount, setDiscount] = useState('0');
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [helpOpen, setHelpOpen] = useState(false);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const { selectedBranch } = useBranch();

  // Load currencies
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await getSupportedCurrencies();
        setCurrencies(data.Currencies || []);
      } catch (error) {
        console.error('Error loading currencies:', error);
        setCurrencies([
          { code: 'YER', symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0 },
          { code: 'YRN', symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0 },
          { code: 'USD', symbol: '$', name: 'دولار أمريكي', decimals: 2 },
          { code: 'SAR', symbol: 'ر.س', name: 'ريال سعودي', decimals: 2 }
        ]);
      }
    };
    loadCurrencies();
  }, []);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line
  }, [selectedBranch]);

  const loadProducts = async () => {
    if (!selectedBranch) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchProducts(selectedBranch.id || selectedBranch.Id);
      const filtered = data.filter(p => {
        const cat = p.categoryName || p.CategoryName || '';
        return !cat.startsWith("العروض:") && 
               !cat.startsWith("مجموعة:") &&
               cat !== "Offer" &&
               cat !== "Deal" &&
               (!Array.isArray(p.SubProducts) || p.SubProducts.length === 0);
      });
      setProducts(filtered);
    } catch (error) {
      console.error('Error loading products:', error);
      setNotification({ open: true, message: t('loadProductsError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Stable helper functions
  const getCurrencySymbol = useCallback((code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : code;
  }, [currencies]);

  const getCurrencyInfo = useCallback((code) => {
    return currencies.find(c => c.code === code) || { code, symbol: code, name: code, decimals: 2 };
  }, [currencies]);

  const getProductCurrencyPrices = useCallback((product) => {
    if (product.CurrencyPrices && typeof product.CurrencyPrices === 'object') {
      return product.CurrencyPrices;
    }
    const currency = product.currency || product.Currency || 'YER';
    const price = product.price || product.Price || 0;
    return { [currency]: price };
  }, []);

  // ✅ Stable handlers
  const handleToggleProduct = useCallback((product) => {
    const productId = product.id || product.Id;
    setSelectedProducts(prev => {
      const exists = prev.find(p => (p.id || p.Id) === productId);
      if (exists) {
        return prev.filter(p => (p.id || p.Id) !== productId);
      } else {
        return [...prev, { ...product, quantity: 1 }];
      }
    });
  }, []);

  const handleQuantityChange = useCallback((id, delta) => {
    setSelectedProducts(prev => prev.map(p => {
      if ((p.id || p.Id) === id) {
        const newQty = Math.max(1, p.quantity + delta);
        return { ...p, quantity: newQty };
      }
      return p;
    }));
  }, []);

  // ✅ Stable event handlers for inputs
  const handleGroupNameChange = useCallback((e) => {
    setGroupName(e.target.value);
  }, []);

  const handleDiscountChange = useCallback((e) => {
    setDiscount(e.target.value);
  }, []);

  const handleImageChange = useCallback((e) => {
    setImageFile(e.target.files[0]);
  }, []);

  // ✅ Memoized calculations
  const totalsPerCurrency = useMemo(() => {
    const currencyTotals = {};
    selectedProducts.forEach(p => {
      const prices = getProductCurrencyPrices(p);
      const qty = p.quantity;
      Object.keys(prices).forEach(currency => {
        if (!currencyTotals[currency]) {
          currencyTotals[currency] = 0;
        }
        currencyTotals[currency] += parseFloat(prices[currency]) * qty;
      });
    });
    return currencyTotals;
  }, [selectedProducts, getProductCurrencyPrices]);

  const discountedPrices = useMemo(() => {
    const discounted = {};
    Object.keys(totalsPerCurrency).forEach(currency => {
      const currInfo = getCurrencyInfo(currency);
      const original = totalsPerCurrency[currency];
      const discountAmount = (original * parseFloat(discount || 0)) / 100;
      const finalPrice = original - discountAmount;
      discounted[currency] = parseFloat(finalPrice.toFixed(currInfo.decimals));
    });
    return discounted;
  }, [totalsPerCurrency, discount, getCurrencyInfo]);

  const usedCurrencies = useMemo(() => {
    const currencySet = new Set();
    selectedProducts.forEach(p => {
      const prices = getProductCurrencyPrices(p);
      Object.keys(prices).forEach(currency => currencySet.add(currency));
    });
    return Array.from(currencySet);
  }, [selectedProducts, getProductCurrencyPrices]);

  const handleSubmit = useCallback(async () => {
    if (!selectedBranch) {
      setNotification({ open: true, message: t('selectBranchFirst'), severity: 'warning' });
      return;
    }
    if (selectedProducts.length < 2) {
      setNotification({ open: true, message: t('selectAtLeastTwoProducts'), severity: 'warning' });
      return;
    }
    if (!groupName) {
      setNotification({ open: true, message: t('enterGroupName'), severity: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      let imageJson = null;
      if (imageFile) {
        const reader = new FileReader();
        imageJson = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(imageFile);
        });
      }

      const payload = {
        NewProductName: groupName,
        Discount: parseFloat(discount),
        ProductIds: selectedProducts.map(p => p.id || p.Id),
        Quantities: selectedProducts.map(p => p.quantity),
        ImageJson: imageJson,
        BranchId: selectedBranch.id || selectedBranch.Id
      };

      await createGroupedProduct(payload);
      setNotification({ open: true, message: t('offerCreatedSuccess'), severity: 'success' });
      
      setGroupName('');
      setDiscount('0');
      setSelectedProducts([]);
      setImageFile(null);
      setOfferDialogOpen(false);
      loadProducts();
    } catch (error) {
      console.error('Error creating offer:', error);
      setNotification({ open: true, message: t('createOfferError'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  }, [selectedBranch, selectedProducts, groupName, discount, imageFile, t]);

  // Help Dialog Component
  const HelpDialog = React.memo(() => {
    const steps = [
      {
        label: t('helpStep1Label') || 'Step 1: Select Products',
        icon: <LocalOfferIcon color="primary" />,
        description: t('helpStep1Description') || 'Click on products to add them to your grouped offer.',
        tips: [
          t('helpStep1Tip1') || 'Click any product card to select/deselect',
          t('helpStep1Tip2') || 'Selected products show a blue checkmark',
          t('helpStep1Tip3') || 'You need at least 2 products for an offer',
          t('helpStep1Tip4') || 'Products with multiple currencies are supported'
        ]
      },
      {
        label: t('helpStep2Label') || 'Step 2: Click Create Offer',
        icon: <ArrowForwardIcon color="primary" />,
        description: t('helpStep2Description') || 'Click the "Create Offer" button to continue.',
        tips: [
          t('helpStep2Tip1') || 'Button appears after selecting products',
          t('helpStep2Tip2') || 'Opens offer configuration dialog',
          t('helpStep2Tip3') || 'You can adjust quantities inside',
          t('helpStep2Tip4') || 'Set discount and name there'
        ]
      },
      {
        label: t('helpStep3Label') || 'Step 3: Configure Offer',
        icon: <DiscountIcon color="primary" />,
        description: t('helpStep3Description') || 'Set offer details in the dialog.',
        tips: [
          t('helpStep3Tip1') || 'Adjust product quantities',
          t('helpStep3Tip2') || 'Enter discount percentage',
          t('helpStep3Tip3') || 'Add offer name and image',
          t('helpStep3Tip4') || 'See price preview for all currencies'
        ]
      },
      {
        label: t('helpStep4Label') || 'Step 4: Save Offer',
        icon: <SaveIcon color="success" />,
        description: t('helpStep4Description') || 'Review and create your offer.',
        tips: [
          t('helpStep4Tip1') || 'Check all details before saving',
          t('helpStep4Tip2') || 'Offer goes live immediately',
          t('helpStep4Tip3') || 'Multi-currency prices calculated automatically',
          t('helpStep4Tip4') || 'You can edit or delete later'
        ]
      }
    ];

    return (
      <Dialog 
        open={helpOpen} 
        onClose={() => setHelpOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' } // ✅ Increased xs from 60px to 100px
          }
        }}
        PaperProps={{
          sx: { 
            borderRadius: isSmallMobile ? 0 : 2,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' }, // ✅ Adjusted for mobile
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: { xs: 2, sm: 2.5 }
        }}>
          <HelpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('helpDialogTitle') || 'How to Create Grouped Offers'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={() => setHelpOpen(false)} 
            sx={{ color: 'white' }}
            size={isSmallMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              {t('helpWhatIsGroupedProduct') || 'What is a Grouped Product?'}
            </Typography>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
            >
              {t('helpWhatIsGroupedProductDesc') || 'A grouped product combines multiple items into one offer with a special discount. Perfect for combos and bundles!'}
            </Typography>
          </Alert>

          <List sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Box key={index}>
                <ListItem alignItems="flex-start" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ mt: 1, minWidth: { xs: 40, sm: 56 } }}>
                    <Box sx={{ 
                      bgcolor: 'primary.light', 
                      borderRadius: '50%', 
                      p: { xs: 1, sm: 1.5 }, 
                      display: 'flex',
                      boxShadow: 1
                    }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary" 
                        sx={{ 
                          mb: 1,
                          fontSize: { xs: '0.95rem', sm: '1.1rem' }
                        }}
                      >
                        {step.label}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }
                          }}
                        >
                          {step.description}
                        </Typography>
                        <Box sx={{ bgcolor: '#f5f5f5', p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            fontWeight="bold" 
                            color="primary" 
                            sx={{ 
                              mb: 1, 
                              display: 'block',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' }
                            }}
                          >
                            💡 {t('helpTips') || 'Tips'}
                          </Typography>
                          {step.tips.map((tip, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ 
                                fontSize: { xs: 14, sm: 16 }, 
                                mr: 1, 
                                color: 'success.main', 
                                mt: 0.3 
                              }} />
                              <Typography 
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                              >
                                {tip}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              ⚠️ {t('helpImportantNotes') || 'Important Notes'}
            </Typography>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
            >
              <Box sx={{ mt: 1 }}>
                • {t('helpImportantNote1') || 'Multi-currency products are fully supported'}<br/>
                • {t('helpImportantNote2') || 'Discount applies to all currencies automatically'}<br/>
                • {t('helpImportantNote3') || 'Minimum 2 products required for offers'}<br/>
                • {t('helpImportantNote4') || 'Grouped products appear in separate tab'}
              </Box>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#f9f9f9' }}>
          <Button 
            onClick={() => setHelpOpen(false)} 
            variant="contained" 
            color="primary" 
            fullWidth={isSmallMobile}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('helpGotIt') || 'Got it!'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      p: { xs: 2, sm: 2.5, md: 3 }
    }}>
      <HelpDialog />

      {/* ✅ FIXED: Offer Dialog - simplified structure */}
     <Dialog 
        open={offerDialogOpen} 
        onClose={() => setOfferDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' } // ✅ Increased xs from 60px to 100px
          }
        }}
        PaperProps={{
          sx: { 
            borderRadius: isSmallMobile ? 0 : 2,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' }, // ✅ Adjusted for mobile
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'success.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: { xs: 2, sm: 2.5 }
        }}>
          <LocalOfferIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('createOffer') || 'Create Grouped Offer'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={() => setOfferDialogOpen(false)} 
            sx={{ color: 'white' }}
            size={isSmallMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          {/* Selected Products List */}
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('selectedProducts')} ({selectedProducts.length})
          </Typography>
          <Box sx={{ mb: 3, maxHeight: 300, overflow: 'auto' }}>
            {selectedProducts.map(p => {
              const prices = getProductCurrencyPrices(p);
              const pCurrencies = Object.keys(prices);
              const primaryCurr = pCurrencies[0];
              
              return (
                <Paper 
                  key={p.id || p.Id} 
                  elevation={1}
                  sx={{ 
                    p: { xs: 1.5, sm: 2 },
                    mb: 1.5,
                    border: '1px solid #eee'
                  }}
                >
                  <Stack 
                    direction="row" 
                    justifyContent="space-between" 
                    alignItems="center"
                    spacing={1}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="body2" 
                        noWrap 
                        title={p.productName || p.ProductName}
                        sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}
                      >
                        {p.productName || p.ProductName}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        {getCurrencySymbol(primaryCurr)}{prices[primaryCurr]}
                        {pCurrencies.length > 1 && <span> +{pCurrencies.length - 1}</span>}
                      </Typography>
                    </Box>
                    
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleQuantityChange(p.id || p.Id, -1)}
                        sx={{ p: { xs: 0.5, sm: 0.8 } }}
                      >
                        <RemoveIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                      </IconButton>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ 
                          minWidth: { xs: 20, sm: 24 },
                          textAlign: 'center',
                          fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}
                      >
                        {p.quantity}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => handleQuantityChange(p.id || p.Id, 1)}
                        sx={{ p: { xs: 0.5, sm: 0.8 } }}
                      >
                        <AddIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                      </IconButton>
                    </Stack>
                    
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleToggleProduct(p)}
                      sx={{ p: { xs: 0.5, sm: 0.8 } }}
                    >
                      <CloseIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    </IconButton>
                  </Stack>
                </Paper>
              );
            })}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ✅ FIXED: Form Fields with stable handlers */}
          <TextField
            fullWidth
            size="small"
            label={t('newProductName') || 'Offer Name'}
            value={groupName}
            onChange={handleGroupNameChange}
            sx={{ 
              mb: 2,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.85rem', sm: '0.9rem' }
              }
            }}
            required
            placeholder={t('enterOfferName') || 'e.g., Family Combo'}
          />
          
          <TextField
            fullWidth
            size="small"
            label={t('discountPercentage') || 'Discount %'}
            type="number"
            value={discount}
            onChange={handleDiscountChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={{ 
              mb: 2,
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.85rem', sm: '0.9rem' }
              }
            }}
            inputProps={{ min: 0, max: 100 }}
          />

          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="group-image-file"
              type="file"
              onChange={handleImageChange}
            />
            <label htmlFor="group-image-file">
              <Button 
                variant="outlined" 
                fullWidth 
                size={isSmallMobile ? "small" : "medium"}
                component="span" 
                startIcon={<CloudUploadIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                color={imageFile ? 'success' : 'primary'}
                sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                {imageFile ? `✓ ${t('imageSelected') || 'Image Selected'}` : (t('addGroupImage') || 'Add Image')}
              </Button>
            </label>
          </Box>

          {usedCurrencies.length > 0 && (
            <Paper 
              elevation={1} 
              sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                mb: 2, 
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: 1 
              }}
            >
              <Typography 
                variant="subtitle2" 
                fontWeight="bold" 
                sx={{ 
                  mb: 1, 
                  color: 'primary.dark',
                  fontSize: { xs: '0.85rem', sm: '0.9rem' }
                }}
              >
                💰 {t('totalPrices') || 'Total Prices'}:
              </Typography>
              <Table size="small">
                <TableBody>
                  {usedCurrencies.map(currency => {
                    const original = totalsPerCurrency[currency];
                    const discounted = discountedPrices[currency];
                    const currSymbol = getCurrencySymbol(currency);
                    const hasDiscount = parseFloat(discount) > 0;
                    
                    return (
                      <TableRow key={currency}>
                        <TableCell 
                          sx={{ 
                            py: 0.5, 
                            border: 'none', 
                            fontWeight: 'bold',
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }
                          }}
                        >
                          {currency}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            py: 0.5, 
                            border: 'none', 
                            textAlign: 'right' 
                          }}
                        >
                          {hasDiscount && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                textDecoration: 'line-through', 
                                color: 'text.secondary', 
                                display: 'block',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              {currSymbol}{original.toFixed(getCurrencyInfo(currency).decimals)}
                            </Typography>
                          )}
                          <Typography 
                            variant="body2" 
                            fontWeight="bold" 
                            color={hasDiscount ? 'error' : 'inherit'}
                            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                          >
                            {currSymbol}{discounted.toFixed(getCurrencyInfo(currency).decimals)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#f9f9f9' }}>
          <Button 
            onClick={() => setOfferDialogOpen(false)} 
            variant="outlined"
            color="error"
            fullWidth={isSmallMobile}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            color="success" 
            disabled={submitting || selectedProducts.length < 2 || !groupName}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            fullWidth={isSmallMobile}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {submitting ? (t('creating') || 'Creating...') : (t('createOffer') || 'Create Offer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header with both buttons */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between"
        spacing={{ xs: 2, sm: 0 }}
        mb={{ xs: 2, sm: 3 }}
      >
        <Typography 
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
        >
          {t('groupProductsIntoOffer') || 'Group Products Into Offer'}
        </Typography>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Tooltip title={t('helpButtonTooltip') || 'Click for help'} arrow>
            <Button
              variant="contained"
              color="info"
              startIcon={<HelpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              onClick={() => setHelpOpen(true)}
              fullWidth={isSmallMobile}
              size={isSmallMobile ? "medium" : "large"}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6
                }
              }}
            >
              {t('helpButtonText') || 'Help'}
            </Button>
          </Tooltip>

          <Tooltip 
            title={selectedProducts.length < 2 ? (t('selectAtLeastTwoProducts') || 'Select at least 2 products') : (t('createOffer') || 'Create Offer')} 
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="success"
                startIcon={<LocalOfferIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                onClick={() => setOfferDialogOpen(true)}
                disabled={selectedProducts.length < 2}
                fullWidth={isSmallMobile}
                size={isSmallMobile ? "medium" : "large"}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6
                  }
                }}
              >
                {t('createOffer') || 'Create Offer'}
                {selectedProducts.length > 0 && ` (${selectedProducts.length})`}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('selectBranchToCreateGroupedProduct') || 'Please select a branch first'}
        </Alert>
      )}

      {usedCurrencies.length > 1 && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography 
            variant="body2" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            💰 {t('multiCurrencyDetected') || 'Multi-currency offer detected!'}
          </Typography>
          <Typography 
            variant="caption"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
          >
            {t('multiCurrencyInfo') || `Your selected products have prices in ${usedCurrencies.length} currencies: ${usedCurrencies.join(', ')}. The offer will be available in all currencies.`}
          </Typography>
        </Alert>
      )}

      {selectedProducts.length >= 2 && (
        <Grow in={true}>
          <Alert 
            severity="success" 
            icon={<ArrowForwardIcon />}
            sx={{ 
              mb: 3,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.02)' }
              }
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              ✅ {selectedProducts.length} {t('productsSelected') || 'products selected'}! 
              {' '}{t('clickCreateOfferToContinue') || 'Click "Create Offer" button to continue'} 👆
            </Typography>
          </Alert>
        </Grow>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={isSmallMobile ? 50 : 60} />
        </Box>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {products.map(p => (
            <ProductCard 
              key={p.id || p.Id} 
              product={p}
              isSelected={selectedProducts.some(sp => (sp.id || sp.Id) === (p.id || p.Id))}
              onToggle={handleToggleProduct}
              getCurrencySymbol={getCurrencySymbol}
              getProductCurrencyPrices={getProductCurrencyPrices}
              isSmallMobile={isSmallMobile}
            />
          ))}
        </Grid>
      )}

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: 'center'
        }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity} 
          sx={{ 
            width: '100%',
            fontSize: { xs: '0.85rem', sm: '0.9rem' }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}